<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center mb-3 d-print-none">
          <div style="flex: 1">
            <v-btn text @click="goBack">
              <v-icon left>mdi-arrow-left</v-icon>
              Back to List
            </v-btn>
          </div>
          <div class="text-center" style="flex: 1">
            <template v-if="renderedEmail && renderedEmail.templateId">
              <span class="text-h6">{{ renderedEmail.templateId }}</span>
              <span v-if="renderedEmail.version" class="ml-2 text-caption grey--text">
                ({{ formatVersionDate(renderedEmail.version) }})
              </span>
            </template>
          </div>
          <div class="d-flex justify-end" style="flex: 1">
            <v-btn
              v-if="renderedEmail && renderedEmail.templateId"
              small
              outlined
              :href="templateEditorUrl"
              target="_blank"
              title="Open template in editor"
            >
              <v-icon small left>mdi-pencil</v-icon>
              Edit Template
            </v-btn>
          </div>
        </div>

        <v-alert v-if="error" type="error" dismissible @input="error = null" class="d-print-none">
          {{ error }}
        </v-alert>

        <v-alert
          v-if="renderedEmail && renderedEmail.errors && renderedEmail.errors.length"
          type="warning"
          class="mb-3 d-print-none"
        >
          <div v-for="(err, i) in parsedErrors" :key="i" :class="{'mt-2': i > 0}">
            <template v-if="err.parsed">
              <strong v-if="err.line">Line {{ err.line }}<span v-if="err.tagName"> &lt;{{
                  err.tagName
                }}&gt;</span>:</strong>
              {{ err.message }}
            </template>
            <template v-else>{{ err.raw }}</template>
          </div>
        </v-alert>

        <v-card v-if="renderedEmail" outlined>
          <v-divider class="d-print-none"/>
          <v-card-text class="pt-2 pb-0 d-print-none">
            <v-row no-gutters>
              <v-col>
                <v-row no-gutters>
                  <v-col>
                    <strong>To:</strong>
                    <customer-header v-if="customer" :customer="customer"/>
                    <template v-else>{{ renderedEmail.to }}</template>
                    <br>
                    <strong>Subject:</strong> {{ renderedEmail.subject }}<br>
                  </v-col>
                  <v-col class="pl-4">
                    <strong>Sent:</strong> {{ formatDate(renderedEmail.sentAt) }}
                    <template v-if="emailStats && emailStats.Status" class="d-flex align-center">
                      <v-chip small :color="getStatusColor(emailStats.Status)">
                        <v-icon small left>{{ getStatusIcon(emailStats.Status) }}</v-icon>
                        {{ emailStats.Status }}
                      </v-chip>
                    </template>
                    <template v-else>
                      <v-chip x-small :color="renderedEmail.sendSuccess ? 'success' : 'error'" class="ml-1">
                        {{ renderedEmail.sendSuccess ? 'Sent' : 'Failed' }}
                      </v-chip>
                    </template>
                    <br>
                    <strong>From:</strong> {{ renderedEmail.fromAddress }}<br>
                  </v-col>
                </v-row>
                <div
                  v-if="emailStats && (emailStats.ArrivedAt || emailStats.Delay !== undefined) || renderedEmail.messageId"
                  class="text-caption grey--text d-flex justify-space-between">
                  <div v-if="renderedEmail.messageId">
                    <strong>Message ID:</strong> {{ renderedEmail.messageId }}
                  </div>
                  <div v-if="emailStats && emailStats.ArrivedAt && formatDate(emailStats.ArrivedAt)!==formatDate(renderedEmail.sentAt)" >
                    <strong>Delivered:</strong> {{ formatDate(emailStats.ArrivedAt) }}
                  </div>
                  <div v-if="emailStats && emailStats.Delay !== undefined && emailStats.Delay > 0" >
                    <strong>Delay:</strong>
                    <span :class="{ 'error--text font-weight-bold': emailStats.Delay > 300 }">{{
                        formatDelay(emailStats.Delay)
                      }}</span>
                    <v-icon v-if="emailStats.Delay > 300" small color="error" class="ml-1">mdi-alert</v-icon>
                  </div>

                </div>
              </v-col>
              <v-col cols="auto" class="pl-4 d-flex flex-column align-end" style="gap: 8px;">
                <div class="d-flex align-center" style="gap: 8px;">
                  <v-btn-toggle v-model="viewportMode" mandatory dense>
                    <v-btn small value="desktop" title="Desktop view">
                      <v-icon small>mdi-monitor</v-icon>
                    </v-btn>
                    <v-btn small value="mobile" title="Mobile view">
                      <v-icon small>mdi-cellphone</v-icon>
                    </v-btn>
                  </v-btn-toggle>
                  <v-btn
                    small
                    :outlined="!darkMode"
                    :color="darkMode ? 'grey darken-3' : ''"
                    :dark="darkMode"
                    @click="darkMode = !darkMode"
                    title="Toggle dark mode preview"
                  >
                    <v-icon small>mdi-weather-night</v-icon>
                  </v-btn>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                  <v-btn x-small outlined @click="showData = !showData" :disabled="!renderedEmail.data">
                    <v-icon x-small left>mdi-code-json</v-icon>
                    {{ showData ? 'Hide' : 'Show' }} Data
                  </v-btn>
                  <v-btn x-small outlined @click="copyData" :disabled="!renderedEmail.data">
                    <v-icon x-small left>{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                    {{ copied ? 'Copied' : 'Copy Data' }}
                  </v-btn>
                </div>

              </v-col>
            </v-row>
            <pre v-if="showData" class="mt-2 pa-2 data-json">{{ JSON.stringify(renderedEmail.data, null, 2) }}</pre>
          </v-card-text>
          <v-divider v-if="renderedEmail.html" class="d-print-none"/>
          <v-card-text v-if="renderedEmail.html" class="pa-0">
            <div
              class="email-preview-container"
              :style="`background: ${darkMode ? '#1a1a1a' : '#f5f5f5'}; display: flex; justify-content: center;`"
            >
              <div :class="viewportMode === 'mobile' ? 'mobile-viewport' : 'desktop-viewport'">
                <iframe
                  ref="emailFrame"
                  :srcdoc="previewHtml"
                  frameborder="0"
                  :style="`width: 100%; min-height: 600px; background: ${darkMode ? '#1a1a1a' : 'white'};`"
                />
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-progress-linear v-else-if="loading" indeterminate color="primary" class="mt-4"/>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import api from '../api';
import moment from 'moment';
import {dateFormatted} from '../store/utils';
import CustomerHeader from '../components/CustomerHeader.vue';

export default {
  name: 'CustomerEmailDetail',
  components: {CustomerHeader},
  props: {
    logId: {type: String, required: true},
  },
  data() {
    return {
      renderedEmail: null,
      emailStats: null,
      customer: null,
      loading: false,
      error: null,
      viewportMode: 'desktop',
      darkMode: false,
      showData: false,
      copied: false,
    };
  },
  computed: {
    templateEditorUrl() {
      if (!this.renderedEmail || !this.renderedEmail.templateId) return '';
      const route = this.$router.resolve({
        name: 'EmailTemplateEditor',
        query: {template: this.renderedEmail.templateId},
      });
      return route.href;
    },
    parsedErrors() {
      if (!this.renderedEmail || !this.renderedEmail.errors) return [];
      return this.renderedEmail.errors.map(err => {
        try {
          const obj = typeof err === 'string' ? JSON.parse(err) : err;
          if (obj && typeof obj === 'object' && obj.message) {
            return {parsed: true, line: obj.line, tagName: obj.tagName, message: obj.message};
          }
        } catch (e) { /* not JSON */
        }
        return {parsed: false, raw: err};
      });
    },
    previewHtml() {
      if (!this.renderedEmail || !this.renderedEmail.html || !this.darkMode) {
        return this.renderedEmail && this.renderedEmail.html;
      }
      const darkStyle = `<style>
        :root { color-scheme: dark; }
        html, body { background-color: #1a1a1a !important; }
      </style>`;
      return this.renderedEmail.html.replace('</head>', darkStyle + '</head>');
    },
  },
  async mounted() {
    await this.loadEmail();
  },
  methods: {
    async loadEmail() {
      this.loading = true;
      this.error = null;

      try {
        const [renderResponse, statsResponse] = await Promise.all([
          api.get(`/v2/email-log/${this.logId}/render`),
          api.get(`/v2/email-log/${this.logId}/stats`).catch(err => {
            console.warn('Failed to load email stats:', err);
            return {data: {tracked: false}};
          }),
        ]);

        this.renderedEmail = renderResponse.data;
        this.emailStats = statsResponse.data.tracked ? statsResponse.data : null;
        this.loadCustomer();

        this.$nextTick(() => {
          if (this.$refs.emailFrame) {
            this.$refs.emailFrame.onload = () => {
              try {
                const iframeDoc = this.$refs.emailFrame.contentDocument;
                if (iframeDoc && iframeDoc.body) {
                  this.$refs.emailFrame.style.height = iframeDoc.body.scrollHeight + 'px';
                }
              } catch (e) {
                console.error('Error adjusting iframe height:', e);
              }
            };
          }
        });
      } catch (error) {
        console.error('Failed to load email:', error);
        this.error = error.response?.data?.error || 'Failed to load email';
      } finally {
        this.loading = false;
      }
    },

    async loadCustomer() {
      this.customer = null;
      const email = this.renderedEmail && this.renderedEmail.to;
      if (!email) return;

      try {
        const response = await api.get('v2/customers/search', {
          params: {query: email, includeDeleted: true, includeUnconfirmed: true},
        });
        const matches = response.data || [];
        this.customer = matches.find(c => (c.email || '').toLowerCase() === email.toLowerCase()) || null;
      } catch (error) {
        console.warn('Failed to look up customer for email:', email, error);
      }
    },

    async copyData() {
      await navigator.clipboard.writeText(JSON.stringify(this.renderedEmail.data, null, 2));
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 2000);
    },

    goBack() {
      const email = this.renderedEmail && this.renderedEmail.to;
      this.$router.push({name: 'CustomerEmailLog', query: email ? {email} : {}});
    },

    formatDate(isoString) {
      return dateFormatted(isoString, {formatString: 'LLL'});
    },

    formatVersionDate(version) {
      if (!version) return '';
      const match = version.match(/v_(\d+)/);
      if (!match) return version;
      return moment(parseInt(match[1], 10)).format(moment.HTML5_FMT.DATETIME_LOCAL);
    },

    getStatusColor(status) {
      const s = (status || '').toLowerCase();
      if (s === 'opened') return 'info';
      if (s === 'clicked') return 'purple';
      if (s.includes('bounce')) return 'warning';
      if (s === 'spam') return 'orange';
      if (s === 'unsub') return 'grey';
      if (s === 'sent') return 'success';
      return 'grey';
    },

    getStatusIcon(status) {
      const s = (status || '').toLowerCase();
      if (s === 'opened') return 'mdi-email-open';
      if (s === 'clicked') return 'mdi-cursor-default-click';
      if (s.includes('bounce')) return 'mdi-email-alert';
      if (s === 'spam') return 'mdi-alert';
      if (s === 'unsub') return 'mdi-account-cancel';
      if (s === 'sent') return 'mdi-email-check';
      return 'mdi-email';
    },

    formatDelay(delaySeconds) {
      if (delaySeconds === 0) return '0 seconds';
      const minutes = Math.floor(delaySeconds / 60);
      const seconds = delaySeconds % 60;
      if (minutes === 0) return `${seconds} second${seconds !== 1 ? 's' : ''}`;
      if (seconds === 0) return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
      return `${minutes} minute${minutes !== 1 ? 's' : ''}, ${seconds} second${seconds !== 1 ? 's' : ''}`;
    },
  },
};
</script>

<style scoped>
.data-json {
  font-size: 12px;
  background: #f5f5f5;
  border-radius: 4px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.email-preview-container {
  padding: 16px;
}

.mobile-viewport {
  width: 375px;
  min-width: 375px;
}

.desktop-viewport {
  width: 100%;
}
</style>
